/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { IndianRupee } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Invoice, type InvoiceStatus } from '../../lib/api/finance.js';
import { useInvoices } from '../../lib/queries/finance.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { formatInr } from '../../lib/utils/money.js';
import { PayNowModal } from '../finance/pay-now-modal.js';

const STATUS_TONE: Readonly<Record<InvoiceStatus, BadgeTone>> = {
  draft: 'slate',
  sent: 'violet',
  partial: 'amber',
  paid: 'green',
  overdue: 'rose',
  void: 'slate',
};

function InvoiceCard({
  invoice,
  onPay,
}: {
  readonly invoice: Invoice;
  readonly onPay: (inv: Invoice) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const settled = invoice.status === 'paid' || invoice.status === 'void';
  const onClick = useCallback(() => {
    onPay(invoice);
  }, [onPay, invoice]);
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div>
        <p className="font-semibold text-slate-800 dark:text-white">{invoice.invoiceNumber}</p>
        <p className="text-sm text-slate-400">
          {invoice.dueDate !== null
            ? t('parent.billing.due', { date: formatIst(invoice.dueDate, 'MMM d') })
            : '—'}
        </p>
      </div>
      <div className="flex items-center gap-4">
        <span className="font-semibold text-slate-800 dark:text-white">
          {formatInr(invoice.amountPaise)}
        </span>
        <Badge label={t(`invoiceStatus.${invoice.status}`)} tone={STATUS_TONE[invoice.status]} />
        {!settled && (
          <button
            type="button"
            onClick={onClick}
            className="focus-ring inline-flex items-center gap-1 rounded-xl bg-[var(--accent)] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
          >
            <IndianRupee className="h-3.5 w-3.5" aria-hidden="true" />
            {t('parent.billing.payNow')}
          </button>
        )}
      </div>
    </div>
  );
}

/** Parent → Billing → Invoices tab: simple card rows + pay-now (Figma). */
export function ParentInvoiceList(): JSX.Element {
  const { t } = useTranslation();
  const invoices = useInvoices();
  const [payInvoice, setPayInvoice] = useState<Invoice | null>(null);
  const items = invoices.data ?? [];

  const onPay = useCallback((inv: Invoice) => {
    setPayInvoice(inv);
  }, []);
  const onClose = useCallback(() => {
    setPayInvoice(null);
  }, []);

  return (
    <>
      <QueryState
        isLoading={invoices.isLoading}
        isError={invoices.isError}
        isEmpty={items.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('parent.billing.empty')}
      >
        <div className="space-y-3">
          {items.map((inv) => (
            <InvoiceCard key={inv.id} invoice={inv} onPay={onPay} />
          ))}
        </div>
      </QueryState>
      <PayNowModal invoice={payInvoice} onClose={onClose} />
    </>
  );
}
